import React, { useId } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import DOMPurify from "dompurify";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowUturnLeftIcon,
  EnvelopeIcon,
  PaperClipIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import ENV from "@config/env";
import { useAppContext } from "@context/AppContext";
import { invalidateNotifications } from "@hooks/query/useAllNotifications";
import {
  deleteNotification,
  markNotificationAsUnread,
} from "@services/notification";
import { formatSchemeDateTime } from "@utils/schemeDay";
import { transformNotification } from "@utils/transformNotification";

/** Size and layout of this page's icon-and-label buttons; add a btn-* colour. */
export const ACTION_BUTTON =
  "items-center gap-1.5 py-1.5 text-xs font-semibold";

/**
 * A notification as this page shows it. The list mixes those the viewer's wallet received with those of the
 * messages it sent (the outbox); a sent one is titled by its recipients rather than by what the recipient sees.
 */
export const describeNotification = (
  notification: any,
  walletId: string | undefined,
  t: TFunction,
) => {
  const item = transformNotification(notification, !notification.viewedAt);
  const message = notification.relatedMessage;
  const received = notification.notifiedWalletId === walletId;
  const recipients =
    message?.recipients?.length > 1
      ? t("notification.recipient_count", { count: message.recipients.length })
      : message?.recipients
          ?.map((r: any) => r.recipientWallet?.name)
          .join(", ");

  return {
    ...item,
    received,
    // A sent one's viewedAt is its recipient's, not the viewer's.
    isNew: received && item.isNew,
    title: received ? item.name : t("notification.sent_to", { recipients }),
    sender: message?.sender
      ? [message.sender.wallet?.name, `<${message.sender.email}>`]
          .filter(Boolean)
          .join(" ")
      : t("audit_trail.system"),
  };
};

/** The selected notification beside the list; assistive tech reads it as a region named by its title. */
const NotificationDetail: React.FunctionComponent<{
  notification: any;
  onClose: () => void;
}> = ({ notification, onClose }) => {
  const { t } = useTranslation();
  const { loggedInInfo } = useAppContext();
  const queryClient = useQueryClient();
  const titleId = useId();
  const item = describeNotification(
    notification,
    loggedInInfo.userDetails?.walletId,
    t,
  );
  const message = notification.relatedMessage;
  const evidence = message?.evidences?.[0];

  // Both close the pane: a dismissed notification is gone, and one left open is
  // read again, which would undo Mark as unread.
  const onSuccess = () => {
    invalidateNotifications(queryClient);
    onClose();
  };
  const { mutate: markAsUnread } = useMutation(
    () => markNotificationAsUnread({ notificationId: notification.id }),
    { onSuccess },
  );
  const { mutate: dismiss, isLoading: isDismissing } = useMutation(
    () => deleteNotification({ notificationId: notification.id }),
    { onSuccess },
  );

  return (
    <section
      aria-labelledby={titleId}
      className="rounded-lg border border-gray-200 bg-white p-6"
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <h2 id={titleId} className="text-base font-bold text-primary-3">
          {item.title}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700"
        >
          <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          <span className="sr-only">{t("common.close")}</span>
        </button>
      </div>
      <p className="text-xs text-gray-500">
        {formatSchemeDateTime(item.date)}
        {item.received ? ` · ${t("common.from")}: ${item.sender}` : null}
      </p>
      <h3 className="mt-4 text-sm font-semibold text-gray-900">
        {item.subject}
      </h3>
      <div
        className="mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-700"
        dangerouslySetInnerHTML={{
          __html: message?.body ? DOMPurify.sanitize(message.body.trim()) : "-",
        }}
      />
      {evidence ? (
        <div className="mt-4 flex w-fit items-center gap-3 rounded border border-gray-200 px-3 py-2 text-sm">
          <PaperClipIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
          {evidence.fileName}
          <a
            href={`${ENV.API_HOST}/evidences/${evidence.id}/download`}
            className="font-semibold text-primary-1 hover:underline"
            download
          >
            {t("common.download")}
          </a>
        </div>
      ) : null}
      {item.received ? (
        <div className="mt-6 flex flex-wrap gap-2">
          <Link
            to={`/zenith/notifications/new?notificationId=${notification.id}`}
            className={`btn-default ${ACTION_BUTTON}`}
          >
            <ArrowUturnLeftIcon className="h-4 w-4" aria-hidden="true" />
            {t("common.reply")}
          </Link>
          <button
            type="button"
            onClick={() => markAsUnread()}
            className={`btn-default ${ACTION_BUTTON}`}
          >
            <EnvelopeIcon className="h-4 w-4" aria-hidden="true" />
            {t("notification.mark_unread")}
          </button>
          <button
            type="button"
            onClick={() => dismiss()}
            disabled={isDismissing}
            className={`btn-default ${ACTION_BUTTON}`}
          >
            <TrashIcon className="h-4 w-4" aria-hidden="true" />
            {t("common.dismiss")}
          </button>
        </div>
      ) : null}
    </section>
  );
};

export default NotificationDetail;
